import { Controller } from "@hotwired/stimulus"

/**
 * Admin Theme Controller - Light/Dark mode toggle with localStorage persistence
 *
 * Features:
 * - Toggle between light and dark themes
 * - Persist preference in localStorage
 * - Apply theme on page load
 * - Update icon dynamically
 * - Respect system preference as default
 */
export default class extends Controller {
  static values = {
    theme: { type: String, default: "light" }
  }

  connect() {
    // Load saved theme or use system preference
    const savedTheme = localStorage.getItem("admin-theme")
    if (savedTheme) {
      this.themeValue = savedTheme
    } else {
      // Check system preference
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
      this.themeValue = prefersDark ? "dark" : "light"
    }

    // Apply theme
    this.applyTheme()
  }

  // Toggle theme
  toggle(event) {
    event?.preventDefault()
    this.themeValue = this.themeValue === "light" ? "dark" : "light"
    this.applyTheme()
    this.saveTheme()
  }

  // Apply theme to document
  applyTheme() {
    const html = document.documentElement

    if (this.themeValue === "dark") {
      html.classList.add("dark")
      this.updateIcon("moon")
    } else {
      html.classList.remove("dark")
      this.updateIcon("sun")
    }
  }

  // Update icon in button
  updateIcon(iconName) {
    // Find the icon element within the button
    const button = this.element
    const iconElement = button.querySelector('[data-controller="admin-icon"]')

    if (iconElement) {
      iconElement.setAttribute("data-admin-icon-name-value", iconName)

      // Trigger icon re-render by dispatching custom event
      iconElement.dispatchEvent(new CustomEvent("admin-icon:update"))
    }
  }

  // Save theme to localStorage
  saveTheme() {
    localStorage.setItem("admin-theme", this.themeValue)
  }

  // Value change callback
  themeValueChanged() {
    this.applyTheme()
  }
}
